import { api, errorText } from "./api";
import { button, clear, el, fmtCount, fmtDate, fmtScore } from "./dom";
import { currentMe, ensureSession } from "./session";
import { type Sheet, openSheet, setStatus, toast } from "./sheet";
import { scoreBar, starField, starText } from "./stars";
import type { PlaceDetail, Rating, RatingInput } from "./types";

export interface PlaceContext {
  /** Called whenever the place's stats may have changed, so the marker can follow. */
  onPlaceChanged(place: PlaceDetail): void;
  /** Called when the sheet closes, so the selection highlight can be dropped. */
  onClosed(): void;
}

/** Opens the place detail sheet; `withForm` jumps straight to the rating form. */
export async function openPlaceSheet(id: string, ctx: PlaceContext, withForm = false): Promise<void> {
  const sheet = openSheet("Betöltés…", () => ctx.onClosed());
  setStatus(sheet, "Betöltés…");
  await reload(sheet, id, ctx, withForm);
}

async function reload(sheet: Sheet, id: string, ctx: PlaceContext, withForm = false): Promise<void> {
  let place: PlaceDetail;
  try {
    place = await api.place(id);
  } catch (err) {
    if (sheet.isOpen()) setStatus(sheet, errorText(err), "error");
    return;
  }
  if (!sheet.isOpen()) return;
  ctx.onPlaceChanged(place);
  if (withForm) renderRatingForm(sheet, place, ctx);
  else renderDetail(sheet, place, ctx);
}

function renderDetail(sheet: Sheet, place: PlaceDetail, ctx: PlaceContext): void {
  sheet.setTitle(place.name);
  clear(sheet.body);

  const s = place.stats;
  sheet.body.append(
    el("div", { class: "place-head" }, [
      el("div", { class: "place-head__score" }, [
        el("strong", { class: "big-score", text: fmtScore(s.avg_overall) }),
        el("span", { class: "big-score__max", text: "/ 5" }),
      ]),
      el("div", { class: "place-head__meta" }, [
        place.address ? el("p", { class: "place-head__address", text: place.address }) : null,
        el("p", { class: "place-head__count", text: fmtCount(s.rating_count) }),
      ]),
    ]),
    el("div", { class: "score-bars" }, [
      scoreBar("Ropogósság", s.avg_crispiness),
      scoreBar("Íz", s.avg_taste),
      scoreBar("Ár-érték", s.avg_value),
    ]),
    myRatingBlock(sheet, place, ctx),
    ratingsBlock(place),
  );
}

function myRatingBlock(sheet: Sheet, place: PlaceDetail, ctx: PlaceContext): HTMLElement {
  const mine = place.my_rating;

  // Rating needs a session. Asking for a nickname replaces this sheet, so in that
  // case the place sheet is re-opened straight into the form afterwards.
  const startRating = (): void => {
    if (currentMe()) {
      renderRatingForm(sheet, place, ctx);
      return;
    }
    void ensureSession("Adj meg egy becenevet, hogy értékelhess.").then((me) => {
      if (me) void openPlaceSheet(place.id, ctx, true);
    });
  };

  if (!mine) {
    return el("div", { class: "my-rating" }, [
      button("Értékelem 🍟", "btn btn--primary btn--wide", startRating),
    ]);
  }

  const remove = button("Törlöm", "btn btn--danger", () => {
    if (!window.confirm("Biztosan törlöd az értékelésedet?")) return;
    api
      .deleteMyRating(place.id)
      .then(() => {
        toast("Értékelés törölve.");
        return reload(sheet, place.id, ctx);
      })
      .catch((err: unknown) => toast(errorText(err), "error"));
  });

  return el("div", { class: "my-rating" }, [
    el("h3", { class: "my-rating__title", text: "Az én értékelésem" }),
    ratingCard(mine, false),
    el("div", { class: "form__actions" }, [
      remove,
      button("Módosítom", "btn btn--primary", startRating),
    ]),
  ]);
}

function ratingsBlock(place: PlaceDetail): HTMLElement {
  const list = el("div", { class: "ratings" });
  const wrap = el("section", { class: "ratings-section" }, [
    el("h3", { class: "ratings-section__title", text: "Értékelések" }),
    list,
  ]);

  if (place.ratings.length === 0) {
    list.append(el("p", { class: "empty", text: "Még senki nem értékelte. Legyél te az első!" }));
    return wrap;
  }
  for (const r of place.ratings) list.append(ratingCard(r, r.mine));

  let cursor = place.next_cursor;
  if (cursor !== null) {
    const more = button("Továbbiak", "btn btn--wide", () => {
      if (cursor === null) return;
      more.disabled = true;
      api
        .ratings(place.id, cursor)
        .then((page) => {
          for (const r of page.ratings) list.insertBefore(ratingCard(r, r.mine), more);
          cursor = page.next_cursor;
          if (cursor === null) more.remove();
          else more.disabled = false;
        })
        .catch((err: unknown) => {
          toast(errorText(err), "error");
          more.disabled = false;
        });
    });
    list.append(more);
  }
  return wrap;
}

function ratingCard(r: Rating, markMine: boolean): HTMLElement {
  return el("article", { class: `rating${markMine ? " rating--mine" : ""}` }, [
    el("header", { class: "rating__head" }, [
      el("span", { class: "rating__who", text: markMine ? `${r.nickname} (te)` : r.nickname }),
      el("span", { class: "rating__overall", text: fmtScore(r.overall) }),
    ]),
    el("p", {
      class: "rating__scores",
      text: `Ropogósság ${starText(r.crispiness)} · Íz ${starText(r.taste)} · Ár-érték ${starText(r.value)}`,
    }),
    r.comment ? el("p", { class: "rating__comment", text: r.comment }) : null,
    el("p", { class: "rating__date", text: fmtDate(r.created_at) }),
  ]);
}

function renderRatingForm(sheet: Sheet, place: PlaceDetail, ctx: PlaceContext): void {
  sheet.setTitle(place.name);
  clear(sheet.body);

  const fields = ratingFields(place.my_rating);
  const error = el("p", { class: "form__error", attrs: { role: "alert" } });
  const submit = el("button", {
    class: "btn btn--primary",
    type: "submit",
    text: place.my_rating ? "Mentés" : "Küldés",
  });

  const form = el("form", { class: "form" }, [
    el("p", { class: "form__hint", text: "Mennyire volt jó a sültkrumpli? Mindhárom szempont kötelező." }),
    ...fields.rows,
    fields.commentLabel,
    fields.comment,
    fields.counter,
    error,
    el("div", { class: "form__actions" }, [
      button("Mégsem", "btn", () => renderDetail(sheet, place, ctx)),
      submit,
    ]),
  ]);

  form.addEventListener("submit", (ev) => {
    ev.preventDefault();
    const input = fields.read();
    if (!input) {
      error.textContent = "Mindhárom szempontot értékeld 1–5 csillaggal.";
      return;
    }
    error.textContent = "";
    submit.disabled = true;
    api
      .putMyRating(place.id, input)
      .then(() => {
        toast("Köszi, elmentettük az értékelésed!");
        return reload(sheet, place.id, ctx);
      })
      .catch((err: unknown) => {
        error.textContent = errorText(err);
        submit.disabled = false;
      });
  });

  sheet.body.append(form);
}

/** The three star rows plus the comment box, shared with the add-place form. */
export function ratingFields(initial: Rating | null = null): {
  rows: HTMLElement[];
  commentLabel: HTMLElement;
  comment: HTMLTextAreaElement;
  counter: HTMLElement;
  read(): RatingInput | null;
  touched(): boolean;
} {
  const crisp = starField("Ropogósság", initial?.crispiness ?? null);
  const taste = starField("Íz", initial?.taste ?? null);
  const value = starField("Ár-érték", initial?.value ?? null);

  const comment = el("textarea", {
    class: "input input--area",
    attrs: { name: "comment", maxlength: "280", rows: "3", placeholder: "Megjegyzés (nem kötelező)" },
  });
  comment.value = initial?.comment ?? "";

  const counter = el("p", { class: "form__note" });
  const paintCounter = (): void => {
    counter.textContent = `${comment.value.length}/280 karakter`;
  };
  comment.addEventListener("input", paintCounter);
  paintCounter();

  return {
    rows: [crisp.row, taste.row, value.row],
    commentLabel: el("label", { class: "form__label", text: "Megjegyzés" }),
    comment,
    counter,
    read(): RatingInput | null {
      const c = crisp.value();
      const t = taste.value();
      const v = value.value();
      if (c === null || t === null || v === null) return null;
      const out: RatingInput = { crispiness: c, taste: t, value: v };
      const text = comment.value.trim();
      if (text !== "") out.comment = text;
      return out;
    },
    touched(): boolean {
      return crisp.value() !== null || taste.value() !== null || value.value() !== null;
    },
  };
}
